import { isEqual, parseISO } from 'date-fns'
import { FonteIdadeGestacionalEnum, Medicao } from 'graphql/types.generated'
import { orderBy } from 'lodash'
import { tipoGravidezRecord } from 'types/enums'
import { isEmpty, isUndefinedOrNull } from 'util/checks'
import { OrigemDum } from 'view/atendimentos/detail/components/modals/medicoes/model-medicoes'
import {
  HistoricoPreNatalModel,
  MedicoesPreNatal,
} from 'view/atendimentos/detail/components/modals/types/model-historicoPreNatal'
import {
  MEDICOES_PRE_NATAL,
  PreNatalCardContentModel,
  PreNatalFormModel,
  TipoPreNatal,
} from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'

import { isInicioOuContinuacaoPreNatal } from '../../../pre-natal/util-preNatal'
import { convertMedicoesAtendimentoAtual, convertMedicoesUltimaGestacao } from '../converter-acompanhamentoPreNatal'
import {
  AcompanhamentoPreNatalCardQueryModel,
  AcompanhamentoPreNatalQueryModel,
  DumStatus,
  IdadeGestacionalAcompanhamentoPreNatal,
  IgStatus,
} from '../model-acompanhamentoPreNatal'

export function getDumAtual(
  atendimentoProfUltimaGestacao: ReadonlyArray<AcompanhamentoPreNatalQueryModel | AcompanhamentoPreNatalCardQueryModel>
): Date {
  for (let i = atendimentoProfUltimaGestacao?.length - 1; i >= 0; i--) {
    const medicoes = atendimentoProfUltimaGestacao[i]?.medicoes
    if (!isEmpty(medicoes) && medicoes[0].dum) {
      return parseISO(medicoes[0].dum)
    }
  }
}

function defineDumComStatus(
  atendimentoProfUltimaGestacao: ReadonlyArray<AcompanhamentoPreNatalQueryModel | AcompanhamentoPreNatalCardQueryModel>,
  dumAtendimentoAtual?: Date
): DumStatus {
  const dumPersistida = getDumAtual(atendimentoProfUltimaGestacao)
  const dumPrioritaria = dumAtendimentoAtual ?? dumPersistida

  const hasDumChanged = !isEqual(dumPersistida, dumPrioritaria)

  let origemDum: OrigemDum = OrigemDum.SEM_DUM
  if (dumAtendimentoAtual && dumPersistida) {
    origemDum = OrigemDum.AMBOS
  } else if (dumAtendimentoAtual) {
    origemDum = OrigemDum.ATENDIMENTO_ATUAL
  } else if (dumPersistida) {
    origemDum = OrigemDum.ATENDIMENTO_ANTERIOR
  }

  return { dumPrioritaria, dumPersistida, origemDum, hasDumChanged }
}

export type MedicaoAtendProfPreNatal = Pick<
  Medicao,
  | 'id'
  | 'dataMedicao'
  | 'valorBatimentoCardiacoFetal'
  | 'valorBatimentoCardiacoFetal2'
  | 'dum'
  | 'valorAlturaUterina'
  | 'valorPressaoArterial'
  | 'valorPeso'
  | 'valorGlicemia'
  | 'medicaoAnterior'
>

function hasMedicoesPreNatalAtendimentoAtual(preNatalAtendimentoAtual: PreNatalFormModel): boolean {
  if (isUndefinedOrNull(preNatalAtendimentoAtual)) return false
  return MEDICOES_PRE_NATAL.some((item) => preNatalAtendimentoAtual[item])
}

function mustShowMedicoesAtendimentoAtual(
  tipoPreNatal: TipoPreNatal,
  pesoAtendimentoAtual: number,
  preNatalAtendimentoAtual: PreNatalFormModel
): boolean {
  return (
    isInicioOuContinuacaoPreNatal(tipoPreNatal) &&
    (!isUndefinedOrNull(pesoAtendimentoAtual) || hasMedicoesPreNatalAtendimentoAtual(preNatalAtendimentoAtual))
  )
}

export interface ConvertDadosPreNatalProps {
  atendimentosProfUltimaGestacao: ReadonlyArray<AcompanhamentoPreNatalQueryModel>
  preNatalAtendimentoAtual?: PreNatalFormModel
  pesoAtendimentoAtual?: number
  dumAtendimentoAtual?: Date
  dataAtendimento?: Instant
  tipoPreNatal?: TipoPreNatal
}

export function convertDadosPreNatal({
  atendimentosProfUltimaGestacao,
  dataAtendimento,
  dumAtendimentoAtual,
  preNatalAtendimentoAtual,
  pesoAtendimentoAtual,
  tipoPreNatal,
}: ConvertDadosPreNatalProps): HistoricoPreNatalModel {
  let medicoes: MedicoesPreNatal[] = []

  atendimentosProfUltimaGestacao?.forEach((atendProf, index) => {
    const medicao = !isEmpty(atendProf.medicoes) ? atendProf.medicoes[0] : null
    const dataMedicao = medicao?.dataMedicao ?? atendProf.iniciadoEm
    medicoes.push(convertMedicoesUltimaGestacao(index, medicao, atendProf.atendimentoProfissionalPreNatal, dataMedicao))
  })

  mustShowMedicoesAtendimentoAtual(tipoPreNatal, pesoAtendimentoAtual, preNatalAtendimentoAtual) &&
    medicoes.push(
      convertMedicoesAtendimentoAtual(dataAtendimento, preNatalAtendimentoAtual, pesoAtendimentoAtual, medicoes?.length)
    )

  medicoes = orderBy(medicoes, 'numeroConsulta', 'desc')

  const dumStatus = defineDumComStatus(atendimentosProfUltimaGestacao, dumAtendimentoAtual)

  return {
    dumStatus,
    medicoesPreNatal: medicoes,
  }
}

const NAO_INFORMADO = 'Não informado'

export function convertAcompanhamentoPreNatalCardQueryToPreNatalCardContentModel(
  atendimentosProfUltimaGestacao: ReadonlyArray<AcompanhamentoPreNatalCardQueryModel>,
  isAltoRiscoAtendimentoAtual: boolean,
  preNatalAtendimentoAtual?: PreNatalFormModel,
  dumAtendimentoAtual?: Date
): PreNatalCardContentModel {
  const atendProfGestacaoMaisRecente = atendimentosProfUltimaGestacao?.last()

  const { tipoGravidez, hasTipoGravidezChanged } = getTipoGravidezStatus(
    preNatalAtendimentoAtual,
    atendProfGestacaoMaisRecente
  )
  const isAltoRisco = isAltoRiscoAtendimentoAtual || getAltoRisco(atendimentosProfUltimaGestacao)
  const dumStatus = defineDumComStatus(atendimentosProfUltimaGestacao, dumAtendimentoAtual)

  return {
    dumStatus,
    dataUltimaConsulta: atendProfGestacaoMaisRecente?.iniciadoEm,
    quantidadeConsultas: atendimentosProfUltimaGestacao?.length,
    isAltoRisco,
    tipoGravidez,
    hasTipoGravidezChanged,
  }
}

interface TipoGravidezStatus {
  tipoGravidez: string
  hasTipoGravidezChanged: boolean
}

export function getTipoGravidezStatus(
  preNatalAtendimentoAtual: PreNatalFormModel,
  atendProfGestacaoMaisRecente: AcompanhamentoPreNatalCardQueryModel
): TipoGravidezStatus {
  const tipoGravidezDatabase =
    atendProfGestacaoMaisRecente?.atendimentoProfissionalPreNatal?.preNatal?.tipoGravidez?.descricao

  const tipoGravidezAtendimento = tipoGravidezRecord[preNatalAtendimentoAtual?.tipoGravidez?.value]

  const hasTipoGravidezChanged =
    !isUndefinedOrNull(tipoGravidezAtendimento) && tipoGravidezAtendimento !== tipoGravidezDatabase

  const tipoGravidez = hasTipoGravidezChanged
    ? tipoGravidezAtendimento ?? NAO_INFORMADO
    : tipoGravidezDatabase ?? NAO_INFORMADO

  return { tipoGravidez, hasTipoGravidezChanged }
}

export function getAltoRisco(atendimentos?: ReadonlyArray<AcompanhamentoPreNatalCardQueryModel>) {
  return atendimentos?.some((atendProf) => atendProf.atendimentoProfissionalPreNatal?.preNatal?.altoRisco)
}

export function getIgStatus(
  idadeGestacional: IdadeGestacionalAcompanhamentoPreNatal,
  hasIgEcoChanged: boolean,
  hasDumChanged: boolean
): IgStatus {
  const isFonteIgEco = idadeGestacional?.fonteIdadeGestacional === FonteIdadeGestacionalEnum.US

  return {
    hasIgChanged: isFonteIgEco ? hasIgEcoChanged : hasDumChanged,
    isFonteIgEco,
  }
}
